import React from 'react';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';
import Button from 'components/core/button';
import GreenCheckIcon from 'src/assets/green-check';
import type { Products } from 'src/gql/graphql';
import { ReviewSummary } from './review-summary';
import { ReviewCard } from './review-card';
import { useProductRatingSummary } from './use-product-rating-summary';
import { useProductReviews } from './use-product-reviews';

type CustomerReviewsProps = {
  product: Products;
};

export default function CustomerReviews({ product }: CustomerReviewsProps): JSX.Element | null {
  const { t } = useTranslation();
  const productId = product.id;

  const { summary } = useProductRatingSummary({ productId });
  const { reviews, totalCount, hasMore, loading, loadMore } = useProductReviews({ productId });

  if (!summary) return null;

  // The review list and the aggregate summary are sourced from different
  // queries; while the list is still loading, fall back to the summary's count
  // so the header doesn't briefly flash "0 Reviews" under a populated summary.
  const reviewCount = totalCount || summary.reviewCount;

  return (
    <Container>
      <ReviewSummary summary={summary} />

      <ReviewsHeader>
        <ReviewsCount>{t('product.reviews.count', '{{count}} Reviews', { count: reviewCount })}</ReviewsCount>
        <VerifiedNote>
          <GreenCheckIcon />
          {t('product.reviews.verified', 'All reviews from verified purchases')}
        </VerifiedNote>
      </ReviewsHeader>

      <ReviewList>
        {reviews.map((review) => (
          <ReviewCard key={review.id} review={review} />
        ))}
      </ReviewList>

      {hasMore && (
        <Button variant='outlinePrimary' size='small' onClick={loadMore} loading={loading}>
          {t('product.reviews.view-more', 'View more reviews')}
        </Button>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
`;

const ReviewsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ReviewsCount = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grey[30]};
`;

const VerifiedNote = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.green[45]};
`;

const ReviewList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
